import { concat, fn, hash } from '@ember/helper';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import { importSync } from '@embroider/macros';
import Component from '@glimmer/component';

import { recordIdentifierFor } from '@warp-drive/core';
import { t } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import { gt, or } from 'ember-truth-helpers';

import InfoBox from 'umg-ember-backend/components/ui/info-box';
import Modal from 'umg-ember-backend/components/ui/modal';
import { isContentItemType } from 'umg-ember-backend/models/content-item';
import storeOnRoute from 'umg-ember-backend/utils/decorators/store-on-route';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import Item from './item';
import Entity from './item/entity';
import Select from './select';
import styles from './styles.module.css';

const bem = getBem(styles);

export default class PageBuilderContentItemsComponet extends Component {
  // Services
  @service breadcrumb;
  @service intl;
  @service history;
  @service store;

  // Defaults
  @storeOnRoute('routeStorageKey') relationToAssign;
  @storeOnRoute('routeStorageKey') positionToAssign;
  @storeOnRoute('routeStorageKey') selectedModelNameToAssign;
  @storeOnRoute('routeStorageKey') selectedEntity;
  @storeOnRoute('routeStorageKey') scrollPositionAssign;
  @storeOnRoute('routeStorageKey') scrollPositionEntity;
  @storeOnRoute('routeStorageKey') showErrors = false;

  // Getter and setter
  get routeStorageKey() {
    return recordIdentifierFor(this.args.parent).lid;
  }

  get direction() {
    return this.args.direction ?? 'y';
  }

  get sortableGroup() {
    return guidFor(this);
  }

  get RecordExplorerForAssign() {
    const type = this.selectedModelNameToAssign ?? 'article';

    const module = importSync(`../../record-explorer/for/${type}`);

    return module.default;
  }

  // Actions
  addContentItem = (relation, position, contentItemType) => {
    this.relationToAssign = relation;
    this.positionToAssign = position;

    if (isContentItemType('placeholder', contentItemType)) {
      this._createContentItem(contentItemType, relation);
    } else if (isContentItemType('module', contentItemType)) {
      this._createAndEditEnity(contentItemType);
    } else {
      this.selectedModelNameToAssign = contentItemType;
    }
  };

  closeAssign = () => {
    this.scrollPositionAssign = 0;
    this.selectedModelNameToAssign = null;
  };

  closeEntity = () => {
    this.selectedEntity = null;
    this.scrollPositionEntity = 0;
  };

  createContentItemFromEntities = (entities) => {
    entities.forEach((entity) => {
      this.createContentItemFromEntity(entity);
    });

    this.selectedModelNameToAssign = null;
  };

  createContentItemFromEntity = (entity) => {
    const entityType = getRecordType(entity);
    let contentItem = this._createContentItem(entityType);

    contentItem.headline = entity.displayHeadline;
    contentItem.contentItemId = entity.id;

    if (
      isContentItemType('module', contentItem.type) &&
      contentItem.type != 'newsletter'
    ) {
      contentItem.entity = entity;
    }

    if (entity?.type && entityType === 'collection') {
      contentItem.collectionType = entity?.type;
    }
  };

  reorderContentItems = (contenItems) => {
    if (this.args.parent.children) {
      this.args.parent.children = contenItems;
    } else {
      this.args.parent.contentItems = contenItems;
    }
  };

  saveEntity = async (entity) => {
    if (this.history.isModelInvalid(entity)) {
      this.showErrors = true;
      return;
    }

    // TODO: REFACTOR-MLP-API If it's not a mlp module, save the entity before saving the mlp. Modules don't have and api endpoint
    if (!entity.isMlpModule) {
      await this.history.saveModel(entity);
    }

    this.createContentItemFromEntity(entity);
    await this.args.onSaveMlp();

    // We need to close the modal to avoid duplicate content items
    this.selectedEntity = null;
  };

  // Functions
  _createAndEditEnity(contentItemType) {
    let entity = this.store.createRecord(contentItemType);

    this.selectedEntity = entity;
  }

  _createContentItem(contentItemType) {
    let modelLabel = this.intl.t(`models.${contentItemType}`);
    let contentItemsOfType = this.args.contentItems.filter((contentItem) => {
      return contentItem.type === contentItemType;
    });

    let contentItem = this.store.createRecord('content-item', {
      headline: `${modelLabel} ${contentItemsOfType.length + 1}`,
      type: contentItemType,
      options: {},
    });

    this.args.contentItems.splice(this.positionToAssign, 0, contentItem);

    return contentItem;
  }

  // Template
  <template>
    {{#if @parent.isExternal}}
      <InfoBox
        @type='warning'
        data-test-page-builder-content-items-external-warning
        class={{bem 'is-external-warning'}}
      >
        {{t 'record_detail.forms.mlp.error_external_page'}}
      </InfoBox>
    {{else}}
      {{#if (or @contentItemTypeOptions @contentItems)}}
        <div
          class={{bem
            (hash
              direction=this.direction
              has-content-items=(gt @contentItems.length 0)
            )
          }}
          {{sortableGroup
            direction=this.direction
            groupName=this.sortableGroup
            onChange=this.reorderContentItems
          }}
          ...attributes
        >
          {{#each @contentItems as |contentItem index|}}
            <Select
              @contentItemTypeOptions={{@contentItemTypeOptions}}
              @isDisabled={{@isDisabled}}
              @isSelectAllowed={{@isSelectAllowed}}
              @onAddContentItem={{fn this.addContentItem @contentItems index}}
              @siblings={{@contentItems}}
              @style='inline-{{this.direction}}'
            />

            <Item
              @contentItem={{contentItem}}
              @contentItems={{@contentItems}}
              @onDuplicateContentItem={{this.duplicateContentItem}}
              @onSelectContentItem={{@onSelectContentItem}}
              @isDisabled={{@isDisabled}}
              @loadLegalContext={{@loadLegalContext}}
              @mlp={{@mlp}}
              @onSaveMlp={{@onSaveMlp}}
              @sortableGroup={{this.sortableGroup}}
              @selectedContentItem={{@selectedContentItem}}
              @showErrors={{@showErrors}}
            />
          {{/each}}

          <Select
            @contentItemTypeOptions={{@contentItemTypeOptions}}
            @isDisabled={{@isDisabled}}
            @isSelectAllowed={{@isSelectAllowed}}
            @onAddContentItem={{fn
              this.addContentItem
              @contentItems
              @contentItems.length
            }}
            @siblings={{@contentItems}}
            @style={{if @contentItems (concat 'inline-' this.direction) null}}
          />
        </div>

        <Entity
          @contentItem={{@contentItem}}
          @entity={{this.selectedEntity}}
          @onBodyScroll={{fn (mut this.scrollPositionEntity)}}
          @onClose={{this.closeEntity}}
          @onSaveEntity={{this.saveEntity}}
          @mlpArtist={{@mlp.artist}}
          @loadLegalContext={{@loadLegalContext}}
          @mlp={{@mlp}}
          @scrollPosition={{this.scrollPositionEntity}}
          @showErrors={{this.showErrors}}
        />

        <Modal
          @onClose={{this.closeAssign}}
          @dataDependencies={{this.selectedModelNameToAssign}}
          as |recordType|
        >
          <this.RecordExplorerForAssign
            @filters={{@filters}}
            @recordType={{recordType}}
            @onAssignRelation={{this.createContentItemFromEntities}}
            @onBodyScroll={{fn (mut this.scrollPositionAssign)}}
            @parentRecordType='mlp'
            @relationType='hasMany'
            @scrollPosition={{this.scrollPositionAssign}}
          />
        </Modal>
      {{/if}}
    {{/if}}
  </template>
}
